import {HTML5AnalyticsStateMachine} from '../../analyticsStateMachines/HTML5AnalyticsStateMachine';
import {Analytics} from '../../core/Analytics';
import VideoCompletionTracker from '../../core/VideoCompletionTracker';
import {Event} from '../../enums/Event';
import {getMIMETypeFromFileExtension} from '../../enums/MIMETypes';
import {Player} from '../../enums/Player';
import {PlayerSize} from '../../enums/PlayerSize';
import {getStreamTypeFromMIMEType} from '../../enums/StreamTypes';
import {Feature} from '../../features/Feature';
import {FeatureConfig} from '../../features/FeatureConfig';
import {AnalyticsConfig} from '../../types/AnalyticsConfig';
import {AnalyticsStateMachineOptions} from '../../types/AnalyticsStateMachineOptions';
import {DrmPerformanceInfo} from '../../types/DrmPerformanceInfo';
import {FeatureConfigContainer} from '../../types/FeatureConfigContainer';
import {PlaybackInfo, normalizeVideoDuration} from '../../types/PlaybackInfo';
import {QualityLevelInfo} from '../../types/QualityLevelInfo';
import {SegmentInfo} from '../../types/SegmentInfo';
import {StreamSources} from '../../types/StreamSources';
import {SubtitleInfo} from '../../types/SubtitleInfo';
import {logger} from '../../utils/Logger';
import {isVideoInFullscreen} from '../../utils/Utils';
import {InternalAdapter} from '../internal/InternalAdapter';
import {InternalAdapterAPI} from '../internal/InternalAdapterAPI';

import HTMLVideoElementStatisticsProvider from './player/HTMLVideoElementStatisticsProvider';

export abstract class HTML5InternalAdapter extends InternalAdapter implements InternalAdapterAPI {
  override get segments(): SegmentInfo[] {
    return [];
  }

  private static BUFFERING_TIMECHANGED_TIMEOUT = 1000;
  mediaElEventHandlers: Array<{event: string; handler: any}>;
  readonly videoCompletionTracker: VideoCompletionTracker;
  protected needsFirstPlayIntent: boolean;
  private onBeforeUnLoadEvent = false;

  private bufferingTimeout?: number;
  private isBuffering: boolean;
  private lastIsLiveStatus: boolean;
  private isPaused: boolean;
  private isSeeking: boolean;
  /** Previous media element time in seconds */
  private previousMediaTimeInSeconds: number;
  /** Previous client time in milliseconds */
  private previousClientTime: number;
  private needsReadyEvent: boolean;
  private mediaElementSet: boolean;
  private previousMutedValue = false;
  private playerStatisticsProvider: HTMLVideoElementStatisticsProvider;

  protected constructor(
    protected mediaElement: HTMLVideoElement | undefined,
    opts?: AnalyticsStateMachineOptions,
  ) {
    super(opts);
    this.initializeStateMachine();
    this.mediaElEventHandlers = [];
    this.bufferingTimeout = undefined;
    this.isBuffering = false;
    this.lastIsLiveStatus = false;
    this.isPaused = false;
    this.isSeeking = false;
    this.previousMediaTimeInSeconds = 0;
    this.previousClientTime = 0;
    this.needsReadyEvent = true;
    this.needsFirstPlayIntent = true;
    this.mediaElementSet = false;
    this.videoCompletionTracker = new VideoCompletionTracker();
    this.playerStatisticsProvider = new HTMLVideoElementStatisticsProvider(mediaElement);
  }

  override resetSourceRelatedState() {
    this.drmPerformanceInfo = undefined;
    this.playerStatisticsProvider.reset();
  }

  getPlayerName = () => Player.HTML5;
  getPlayerTech = () => 'html5';
  getAutoPlay = () => (this.mediaElement ? this.mediaElement.autoplay : false);
  getDrmPerformanceInfo = (): DrmPerformanceInfo | undefined => this.drmPerformanceInfo;

  initialize(analytics: Analytics): Array<Feature<FeatureConfigContainer, FeatureConfig>> {
    if (this.mediaElement) {
      this.setMediaElement();
    }
    this.registerWindowEvents();
    return [];
  }

  isLive = () => (this.mediaElement ? this.mediaElement.duration === Infinity : false);

  // this seems very generic. one could put it in a helper
  // and use it in many adapter implementations.
  getStreamSources(url: string | undefined): StreamSources {
    if (!url) {
      return {};
    }
    const streamType = this.getStreamType();
    switch (streamType) {
      case 'hls':
        return {m3u8Url: url};
      case 'dash':
        return {mpdUrl: url};
      default:
        return {progUrl: url};
    }
  }

  getCurrentPlaybackInfo(): PlaybackInfo {
    let info: PlaybackInfo = {
      ...this.getCommonPlaybackInfo(),
      ...this.getStreamSources(this.getStreamURL()),
      streamFormat: this.getStreamType(),
      isLive: this.isLive(),
      size: isVideoInFullscreen() ? PlayerSize.Fullscreen : PlayerSize.Window,
      playerTech: this.getPlayerTech(),
      droppedFrames: this.playerStatisticsProvider.getDroppedFrames(),
      // TODO: Implement `audioBitrate`
      // TODO: Implement `isCasting`
      // TODO: Implement getting `videoTitle` from player (currently only from the analytics config)
    };

    if (this.mediaElement) {
      info = {
        ...info,
        videoDuration: normalizeVideoDuration(this.mediaElement.duration),
        isMuted: this.mediaElement.muted,
        videoWindowHeight: this.mediaElement.height,
        videoWindowWidth: this.mediaElement.width,
      };
    }

    const qualityInfo = this.getCurrentQualityLevelInfo();
    if (qualityInfo) {
      info = {
        ...info,
        videoPlaybackHeight: qualityInfo.height,
        videoPlaybackWidth: qualityInfo.width,
        videoBitrate: qualityInfo.bitrate,
      };
    }

    return info;
  }

  protected get currentTime(): number {
    if (!this.mediaElement) {
      throw new Error('No media attached');
    }

    return this.mediaElement.currentTime;
  }

  /**
   * Used to setup against the media element.
   * We need this method to desynchronize construction of this class
   * and the actual initialization against the media element.
   * That is because at construction some media engine
   * may not already have the media element attached, for example
   * when passing in the DOM element is happening at once with passing the source URL
   * and can not be decoupled.
   * We are then awaiting an event from the engine and calling this with the media element
   * as argument from our sub-class.
   *
   * This method can also be called without arguments and then it will perform
   * initialization against the existing media element (should only be called once, will throw an error otherwise)
   *
   * It can also be used to replace the element.
   *
   *
   */
  setMediaElement(mediaElement: HTMLVideoElement | null = null) {
    // replace previously existing, if calld with args
    if (mediaElement && this.mediaElement) {
      this.unregisterMediaElement();
      this.mediaElementSet = false;
    }

    // if called without args we assume it's already there
    // we can also be called with args but without any being there before
    if (mediaElement) {
      this.mediaElement = mediaElement;
    }

    if (!this.mediaElement) {
      throw new Error('No media element owned');
    }

    if (this.mediaElementSet) {
      throw new Error('Media element already set (only call this once)');
    }
    this.mediaElementSet = true;

    this.registerMediaElement();
    this.onMaybeReady();
  }

  abstract getPlayerVersion(): string;

  /**
   * Implemented by sub-class to deliver current quality-level info specific to media-engine.
   */
  abstract getCurrentQualityLevelInfo(): QualityLevelInfo | null;

  /**
   * Can be overriden by sub-classes
   * @returns {string}
   *
   */
  getMIMEType(): string | undefined {
    const streamUrl = this.getStreamURL();
    if (!streamUrl || streamUrl === undefined) {
      return;
    }
    return getMIMETypeFromFileExtension(streamUrl);
  }

  /**
   * Can be overriden by sub-classes
   * @returns {string}
   */
  getStreamType(): string | undefined {
    const mimetype = this.getMIMEType();
    if (mimetype) {
      return getStreamTypeFromMIMEType(mimetype);
    }

    return undefined;
  }

  /**
   * Can be overriden by subclasses.
   * @returns {string}
   */
  getStreamURL(): string | undefined {
    const mediaElement = this.mediaElement;
    if (!mediaElement) {
      return;
    }

    return mediaElement.src;
  }

  resetMedia() {
    this.mediaElement = undefined;
    this.mediaElEventHandlers = [];
    window.clearTimeout(this.bufferingTimeout);
  }

  registerMediaElement() {
    const mediaElement = this.mediaElement;
    if (!mediaElement) {
      return;
    }

    this.wrapMediaElementLoad(mediaElement);
    this.listenToMediaElementEvent('loadstart', () => {
      log(`loadstart ${mediaElement.currentTime}`);
      this.eventCallback(Event.READY, {});
    });

    this.listenToMediaElementEvent('loadedmetadata', () => {
      log(`loadedmetadata ${mediaElement.currentTime}`);
      // See https://developer.mozilla.org/en-US/docs/Web/API/HTMLMediaElement/readyState
      // HAVE_NOTHING 0 No information is available about the media resource.
      // HAVE_METADATA 1 Enough of the media resource has been retrieved that
      // the metadata attributes are initialized. Seeking will no longer raise an exception.
      // HAVE_CURRENT_DATA 2 Data is available for the current playback position,
      // but not enough to actually play more than one frame.
      // HAVE_FUTURE_DATA  3 Data for the current playback position as well as for
      //  at least a little bit of time into the future is available
      // (in other words, at least two frames of video, for example).
      // HAVE_ENOUGH_DATA  4 Enough data is available—and the download rate is high
      // enough—that the media can be played through to the end without interruption.
      if (mediaElement.readyState !== 1) {
        // we can't really gather any more information at this point
        return;
      }

      // silent
      this.checkQualityLevelAttributes(true);

      // const {duration, autoplay, width, height, videoWidth, videoHeight, muted} = mediaElement;

      // // This is redundant with what we give to updateMetadata method.
      // // Not sure if there are good reasons to keep that so or if we should better centralize.
      // const info = {
      //   type: 'html5',
      //   isLive: this.isLive(),
      //   version: this.getPlayerVersion(),
      //   streamType: this.getStreamType(),
      //   streamUrl: this.getStreamURL(),
      //   duration,
      //   autoplay,
      //   // HTMLVideoElement.width and HTMLVideoElement.height
      //   // is a DOMString that reflects the height HTML attribute,
      //   // which specifies the height of the display area, in CSS pixels.
      //   // See https://developer.mozilla.org/en-US/docs/Web/API/HTMLVideoElement
      //   width: width,
      //   height: height,
      //   // Returns an unsigned long containing the intrinsic
      //   // height of the resource in CSS pixels,
      //   // taking into account the dimensions, aspect ratio,
      //   // clean aperture, resolution, and so forth,
      //   // as defined for the format used by the resource.
      //   // If the element's ready state is HAVE_NOTHING, the value is 0.
      //   // See https://developer.mozilla.org/en-US/docs/Web/API/HTMLVideoElement
      //   videoWindowWidth: videoWidth,
      //   videoWindowHeight: videoHeight,
      //   muted,
      // };

      // silence events if we have not yet intended play
      // if (this.needsFirstPlayIntent_) {
      //   return;
      // }
      if (this.mediaElement != null) {
        this.videoCompletionTracker.reset();
        this.videoCompletionTracker.setVideoDuration(this.mediaElement.duration);
        this.previousMutedValue = this.mediaElement.muted;
      }
    });

    // We need the PLAY event to indicate the intent to play
    // NOTE: use TIMECHANGED event on 'playing' and trigger PLAY as intended in states.dot graph
    this.listenToMediaElementEvent('play', () => {
      const {currentTime} = mediaElement;

      this.needsFirstPlayIntent = false;

      this.eventCallback(Event.PLAY, {currentTime});
    });

    this.listenToMediaElementEvent('pause', () => {
      this.onPaused();
    });

    this.listenToMediaElementEvent('playing', () => {
      clearTimeout(this.bufferingTimeout);
      this.isPaused = false;
    });

    this.listenToMediaElementEvent('error', () => {
      const {currentTime, error} = mediaElement;

      this.eventCallback(Event.ERROR, {
        currentTime,
        // See https://developer.mozilla.org/en-US/docs/Web/API/MediaError
        code: error?.code ?? undefined,
        message: error?.message ?? undefined,
        data: {},
      });
    });

    this.listenToMediaElementEvent('volumechange', () => {
      const {muted, currentTime, volume} = mediaElement;
      const isMuted = this.isAudioMuted(muted, volume);

      if (this.previousMutedValue !== isMuted) {
        if (isMuted) {
          this.eventCallback(Event.MUTE, {currentTime});
        } else {
          this.eventCallback(Event.UN_MUTE, {currentTime});
        }
        this.previousMutedValue = isMuted;
      }
    });

    this.listenToMediaElementEvent('seeking', () => {
      log(`seeking ${mediaElement.currentTime}`);
      const {currentTime} = mediaElement;

      this.eventCallback(Event.SEEK, {currentTime});
    });

    this.listenToMediaElementEvent('seeked', () => {
      log(`seeked ${mediaElement.currentTime}`);
      const {currentTime} = mediaElement;

      clearTimeout(this.bufferingTimeout);

      this.eventCallback(Event.SEEKED, {currentTime});
    });

    this.listenToMediaElementEvent('timeupdate', () => {
      const {currentTime} = mediaElement;

      this.isBuffering = false;
      this.isSeeking = false;

      // silence events if we have not yet intended play
      if (this.needsFirstPlayIntent) {
        return;
      }

      if (!this.isPaused) {
        this.eventCallback(Event.TIMECHANGED, {currentTime});
      }

      this.checkQualityLevelAttributes();

      this.checkSeeking();

      // We are doing this in case we can not rely
      // on the "stalled" or "waiting" events in a specific browser
      // and to detect intrinsinc paused states (when we do not get a paused event)
      // but the player is paused already before attach or is paused from initialization on.
      this.checkPlayheadProgress();

      this.previousMediaTimeInSeconds = currentTime;
    });

    // The stalled event is fired when the user agent is trying to fetch media data,
    // but data is unexpectedly not forthcoming.
    // https://developer.mozilla.org/en-US/docs/Web/Events/stalled
    this.listenToMediaElementEvent('stalled', () => {
      // this event doesn't indicate buffering by definition (interupted playback),
      // only that data throughput to playout buffers is not as high as expected
      // It happens on Chrome every once in a while as SourceBuffer's are not fed
      // as fast as the underlying native player may prefer (but it does not lead to
      // interuption).
    });

    // The waiting event is fired when playback has stopped because of a temporary lack of data.
    // See https://developer.mozilla.org/en-US/docs/Web/Events/waiting
    this.listenToMediaElementEvent('waiting', () => {
      // we check here for seeking because a programmatically seek where just
      // the currentTime has been changed does not trigger a proper seek event
      log(`waiting ${mediaElement.currentTime}`);
      this.checkSeeking();
      this.onBuffering();
    });
  }

  /**
   * Should only be calld when a mediaElement is attached
   */
  listenToMediaElementEvent(event: any, handler: any) {
    if (!this.mediaElement) {
      throw new Error('No media attached');
    }

    const boundHandler = handler.bind(this);

    this.mediaElEventHandlers.push({event, handler: boundHandler});
    this.mediaElement.addEventListener(event, boundHandler, false);
  }

  onMaybeReady() {
    if (!this.needsReadyEvent || !this.mediaElement) {
      return;
    }

    this.needsReadyEvent = false;

    this.getCurrentPlaybackInfo();

    this.videoCompletionTracker.reset();
    this.videoCompletionTracker.setVideoDuration(this.mediaElement.duration);
    this.eventCallback(Event.READY, {currentTime: this.currentTime});
  }

  /**
   * Should only be calld when a mediaElement is attached
   */
  unregisterMediaElement() {
    if (!this.mediaElement) {
      throw new Error('No media attached');
    }

    const mediaElement = this.mediaElement;

    this.mediaElEventHandlers.forEach((item: {event: string; handler: any}) => {
      mediaElement.removeEventListener(item.event, item.handler);
    });

    this.resetMedia();
  }

  onBuffering() {
    if (!this.mediaElement) {
      throw new Error('No media attached');
    }
    const {currentTime} = this.mediaElement;

    // this handler may be called multiple times
    // for one actual buffering-event occuring so lets guard from
    // triggering this event redundantly.
    if (this.isBuffering || (this.isPaused && !this.isSeeking)) {
      return;
    }

    if (this.isSeeking) {
      this.eventCallback(Event.SEEK, {currentTime});
    } else {
      this.eventCallback(Event.START_BUFFERING, {currentTime});
    }
    this.isBuffering = true;
  }

  onPaused(currentTime?: number) {
    if (this.isPaused) {
      return;
    }
    if (!this.mediaElement) {
      throw new Error('No media attached');
    }

    if (!currentTime) {
      currentTime = this.mediaElement.currentTime;
    }

    this.eventCallback(Event.PAUSE, {currentTime});

    this.isPaused = true;
  }

  registerWindowEvents() {
    this.windowEventTracker.addEventListener('beforeunload', this.onPageClose.bind(this));
    this.windowEventTracker.addEventListener('unload', this.onPageClose.bind(this));
  }

  onPageClose() {
    if (!this.onBeforeUnLoadEvent) {
      this.onBeforeUnLoadEvent = true;
      const mediaElement = this.mediaElement;
      let currentTime: number | undefined;
      if (mediaElement != null) {
        currentTime = mediaElement.currentTime;
      }
      this.eventCallback(Event.UNLOAD, {currentTime});
    }
    this.release();
  }

  checkPlayheadProgress() {
    if (!this.mediaElement) {
      throw new Error('No media attached');
    }
    const mediaElement = this.mediaElement;
    if (mediaElement.paused) {
      this.onPaused();
    }

    clearTimeout(this.bufferingTimeout);

    this.bufferingTimeout = window.setTimeout(() => {
      if (mediaElement.paused || (mediaElement.ended && !this.isBuffering)) {
        return;
      }

      const timeDelta = mediaElement.currentTime - this.previousMediaTimeInSeconds;

      if (timeDelta < HTML5InternalAdapter.BUFFERING_TIMECHANGED_TIMEOUT) {
        this.onBuffering();
      }
    }, HTML5InternalAdapter.BUFFERING_TIMECHANGED_TIMEOUT);
  }

  checkQualityLevelAttributes(silent = false) {
    if (!this.mediaElement) {
      throw new Error('No media attached');
    }

    const mediaElement = this.mediaElement;

    const qualityLevelInfo = this.getCurrentQualityLevelInfo();
    if (!qualityLevelInfo) {
      return;
    }

    const {bitrate, width, height} = qualityLevelInfo;

    const isLive = this.isLive();

    // Detect a change of the isLive status and update the metaData of the SM
    if (isLive !== this.lastIsLiveStatus) {
      this.lastIsLiveStatus = isLive;

      // TODO: Call event for ISLIVE_CHANGE (similar to VIDEO_CHANGE)
      //      The updateMetadata is obsolete, as we introduced the getCurrentPlaybackInfo
      //      (we don't notify the statemachine about state changes, instead we poll the adapter for the information)
      // if (!silent) {
      //   this.stateMachine.updateMetadata();
      // }
    }

    if (bitrate != null && this.qualityChangeService.shouldAllowVideoQualityChange(bitrate)) {
      this.qualityChangeService.setVideoBitrate(bitrate);
      const eventData = {
        width,
        height,
        bitrate,
        currentTime: mediaElement.currentTime,
      };

      if (!silent) {
        this.eventCallback(Event.VIDEO_CHANGE, eventData);
      }
    }
  }

  sourceChange(config: AnalyticsConfig, timestamp: number) {
    this.stateMachine.sourceChange(config, timestamp, this.mediaElement ? this.mediaElement.currentTime : undefined);
  }

  getSelectedSubtitleFromMediaElement(mediaElement: any): SubtitleInfo | undefined {
    if (mediaElement.textTracks == null) {
      return undefined;
    }
    const textTrackList = mediaElement.textTracks;
    for (const attr of textTrackList) {
      if (attr.mode != null && attr.mode === 'showing') {
        const isSubtitleDisplayed = attr.language != null && attr.language.length > 0;
        return {
          enabled: isSubtitleDisplayed,
          language: isSubtitleDisplayed ? attr.language : undefined,
        };
      }
    }
    return {
      enabled: false,
    };
  }

  protected initializeStateMachine(): void {
    this.stateMachine = new HTML5AnalyticsStateMachine(this.stateMachineCallbacks, this.opts);
    this.stateMachine.callEvent(Event.SETUP, {}, 0);
  }

  private wrapMediaElementLoad(mediaElement: HTMLVideoElement) {
    const originalLoad = mediaElement.load;
    mediaElement.load = (...args: Parameters<typeof mediaElement.load>): void => {
      this.sourceChange({}, Date.now());
      originalLoad.apply(mediaElement, args);
    };
  }

  /**
   * Method to detect seeking events on platforms where seeking events are not reliable
   * This code is currently called in timeUpdate event and waiting event.
   * - We can't rely on the `seeking` and 'seeked' events on certain platforms (Tizen, WebOs, Safari, etc.)
   * - Another limitation is that we cannot rely on the time update event to be fired in regular intervals
   *   The time between two timeupdate events can be 1ms or 250ms for example. This code needs to handle this variance.
   */
  private checkSeeking() {
    if (!this.mediaElement) {
      throw new Error('No media attached');
    }
    const currentMediaTimeInSeconds = this.mediaElement.currentTime;
    // absolut difference of playback position compared with last call of method
    const playbackPositionDeltaInSeconds = Math.abs(currentMediaTimeInSeconds - this.previousMediaTimeInSeconds);

    // If the playback position has not changed more than 1 second we ignore it, to avoid
    // issuing of micro seeks. If not ignored the progressThreshold below might be too low, in order to
    // be reliable for such minor seeks. This is a tradeoff between overreporting and underreporting of seek events.
    if (playbackPositionDeltaInSeconds < 1) {
      this.previousClientTime = Date.now();
      return;
    }

    const currentClientTimeInMillis = Date.now();

    // Real time delta of method calls. How much time has passed since last call (in seconds)
    const clientTimeProgressInSeconds = (currentClientTimeInMillis - this.previousClientTime) / 1000;

    // we have to consider the playbackRate when calculating the threshold to trigger seeks
    // playbackRate of 3 progresses the playback triple the speed so the playbackPositionDelta
    // is also higher. This needs to be considered here
    // Adding 0.5 is a safety margin to avoid over reporting of seek events
    const thresholdModifier = this.mediaElement.playbackRate + 0.5;
    const progressThresholdInSeconds = clientTimeProgressInSeconds * thresholdModifier;

    // If the media time (current position of the playback) progresses much faster than real time
    // we consider this a time jump/seek
    if (playbackPositionDeltaInSeconds > progressThresholdInSeconds) {
      this.isSeeking = true;
      this.onPaused(this.previousMediaTimeInSeconds + clientTimeProgressInSeconds);
    }

    this.previousClientTime = Date.now();
  }
}

const log = (msg: string) => {
  const msgTime = new Date().toISOString().substring(11);
  const prefix = `${msgTime}[HTML5InternalAdapter]`;
  const logMsg = `${prefix}: ${msg}`;
  logger.log(logMsg);
};
